import { useState } from "react";
import Select from "react-select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format, startOfWeek, startOfMonth, parseISO } from "date-fns";
import { customSelectStyles } from "../customStyles";
const groupSubscribers = (users, mode) => {
  const grouped = {};

  users?.forEach((u) => {
    if (!u.createdAt) return;
    const date = parseISO(u.createdAt);
    let key;

    if (mode === "week") {
      key = format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd");
    } else if (mode === "month") {
      key = format(startOfMonth(date), "yyyy-MM");
    } else {
      key = format(date, "yyyy-MM-dd");
    }

    if (!grouped[key]) grouped[key] = 0;
    grouped[key] += 1; // count subscribers
  });

  return Object.entries(grouped)
    .map(([period, total]) => ({ period, total }))
    .sort((a, b) => new Date(a.period) - new Date(b.period));
};

const SubscribersChart = ({ users }) => {
  const [mode, setMode] = useState("week");
  const [chartType, setChartType] = useState("line");

  const data = groupSubscribers(users || [], mode);

  const formatLabel = (key) => {
    const date = new Date(key);
    if (mode === "month") return format(date, "MMM yyyy");
    if (mode === "week") return `Week of ${format(date, "MMM d")}`;
    return format(date, "MMM d");
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 10, right: 30, left: 0, bottom: 0 },
    };

    const commonChildren = (
      <>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="period"
          tickFormatter={(tick) =>
            mode === "month"
              ? format(new Date(tick), "MMM")
              : format(new Date(tick), "MMM d")
          }
        />
        <YAxis />
        <Tooltip
          formatter={(value) => [`${value}`, "Subscribers"]}
          labelFormatter={formatLabel}
        />
      </>
    );

    switch (chartType) {
      case "bar":
        return (
          <BarChart {...commonProps}>
            {commonChildren}
            <Bar
              dataKey="total"
              fill="#4CAF50"
              barSize={40}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );
      case "area":
        return (
          <AreaChart {...commonProps}>
            {commonChildren}
            <Area
              type="monotone"
              dataKey="total"
              stroke="#4CAF50"
              fill="#C8E6C9"
            />
          </AreaChart>
        );
      default:
        return (
          <LineChart {...commonProps}>
            {commonChildren}
            <Line
              type="monotone"
              dataKey="total"
              stroke="#4CAF50"
              strokeWidth={2}
            />
          </LineChart>
        );
    }
  };

  // Define options for react-select
  const modeOptions = [
    { value: "day", label: "Daily" },
    { value: "week", label: "Weekly" },
    { value: "month", label: "Monthly" },
  ];

  const chartTypeOptions = [
    { value: "line", label: "Line" },
    { value: "bar", label: "Bar" },
    { value: "area", label: "Area" },
  ];

  return (
    <div className="subscribers-chart">
      <div className="subscribers-chart-header">
        <h3>Subscribers ({mode})</h3>
        <div className="chart-controls">
          <div className="chart-controls">
            <Select
              styles={customSelectStyles} // Add the custom styles here
              value={modeOptions.find((option) => option.value === mode)}
              onChange={(selectedOption) => setMode(selectedOption.value)}
              options={modeOptions}
              isSearchable={false}
            />
            <Select
              styles={customSelectStyles} // Add the custom styles here
              value={chartTypeOptions.find(
                (option) => option.value === chartType
              )}
              onChange={(selectedOption) => setChartType(selectedOption.value)}
              options={chartTypeOptions}
              isSearchable={false}
            />
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default SubscribersChart;
